/**
 * One-click points confirmation (SPEC-ONECLICK-BILLING-001 REQ-OCB-020).
 *
 * Opened by "문제 출제", "OCR 시작" and the widget's one-click retries: shows
 * the expected points next to the current balance and sends the request only
 * after the user confirms. The server's 402 stays the final check; this dialog
 * only blocks when the expected points exceed a balance it actually read.
 */

import { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { PointsDialogFrame } from '@/components/dialogs/PointsPreviewDialog';
import { oneclickApi } from '@/services/oneclickApi';

export const ONECLICK_BALANCE_UNKNOWN_TEXT = '잔액을 확인하지 못했습니다';
export const ONECLICK_FREE_POINTS_TEXT = '0 포인트 (무료)';

export interface OneClickPointsDialogProps {
  open: boolean;
  title: string;
  confirmLabel: string;
  /** Expected points; null when unknown (the caller must then pass a blockReason). */
  points: number | null;
  /** Administrator or impersonation session: shown as free, never blocked on the balance. */
  isFreeSession: boolean;
  /** Reason the request cannot start (failed PDF preflight, OCR retry without pages). */
  blockReason?: string | null;
  warning?: string | null;
  /** Extra pricing line (e.g. the per-passage transform points). */
  notice?: string | null;
  /** Extra content shown first in the notice area (e.g. the passage list being generated). */
  details?: React.ReactNode;
  /**
   * Sends the request. Must not reject: the caller shows its own error. The
   * dialog closes once it settles.
   */
  onConfirm: () => Promise<void>;
  onClose: () => void;
  onPurchase: () => void;
}

export function OneClickPointsDialog({
  open,
  title,
  confirmLabel,
  points,
  isFreeSession,
  blockReason = null,
  warning = null,
  notice = null,
  details = null,
  onConfirm,
  onClose,
  onPurchase,
}: OneClickPointsDialogProps) {
  const [credits, setCredits] = useState<number | null>(null);
  const [isLoadingCredits, setIsLoadingCredits] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Set synchronously on the first confirmation so a second click or shortcut
  // before the next render cannot send another request (REQ-OCB-020 (g)).
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    let disposed = false;
    setIsLoadingCredits(true);
    void oneclickApi.getCredits().then((value) => {
      if (disposed) return;
      setCredits(value);
      setIsLoadingCredits(false);
    });
    return () => {
      disposed = true;
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [open]);

  const isShort = !isFreeSession && credits != null && points != null && points > credits;
  const isBlocked = Boolean(blockReason) || isShort || (!isFreeSession && points == null);
  const canStart = !isBlocked && !isLoadingCredits && !isSubmitting;

  const handleConfirm = async () => {
    if (!canStart || submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      await onConfirm();
    } finally {
      onClose();
    }
  };

  const balanceText = isLoadingCredits
    ? '현재 남은 포인트: 확인 중…'
    : `현재 남은 포인트: ${credits == null ? ONECLICK_BALANCE_UNKNOWN_TEXT : `${credits.toLocaleString()} 포인트`}`;
  const expectedText = `소진 예상 포인트: ${
    isFreeSession
      ? ONECLICK_FREE_POINTS_TEXT
      : points == null
        ? '-'
        : `${warning ? '(최대) ' : ''}${points.toLocaleString()} 포인트`
  }`;

  return (
    <PointsDialogFrame
      open={open}
      title={title}
      balanceText={balanceText}
      expectedText={expectedText}
      balanceShort={isShort}
      confirmText={isSubmitting ? '요청 중…' : confirmLabel}
      confirmDisabled={!canStart}
      shortcutEnabled={!isBlocked}
      onClose={onClose}
      onConfirm={() => void handleConfirm()}
      onPurchase={onPurchase}
      purchaseHref="/store"
      notice={
        <>
          {details}
          {notice && <p className="text-sm text-gray-700">{notice}</p>}
          {warning && <p className="text-sm text-amber-800">{warning}</p>}
          {blockReason && (
            <div role="alert" className="flex items-start gap-2 text-red-700 text-sm">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>{blockReason}</div>
            </div>
          )}
          {isShort && !blockReason && (
            <div role="alert" className="flex items-start gap-2 text-red-700 text-sm">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                {`포인트가 부족합니다. 필요: ${points} 포인트, 보유: ${credits} 포인트. 포인트를 충전한 뒤 시작해 주세요.`}
              </div>
            </div>
          )}
          {isFreeSession && (
            <p className="text-xs text-gray-500">관리자·대리 로그인 세션은 포인트가 차감되지 않습니다.</p>
          )}
        </>
      }
    />
  );
}
